/**
 * Chart embeds for rich-text content.
 *
 * Charts are standalone pages served from this app's `public/embeds/`
 * folder (e.g. `/embeds/aru-basket-vs-gold.html`). The editor inserts an
 * `<iframe>` pointing at one, which the public site renders as-is. The iframe
 * also carries `data-chart="<id>"` so a frontend can later swap it for a
 * native component without touching stored content.
 */

export const CHART_BLOCK_CLASS = 'cms-chart'

export interface ChartBlockConfig {
  /** Slug of the chart; `/embeds/<id>.html` by default. */
  id: string
  /** Accessible title for the iframe (also shown in the editor). */
  title?: string
  /** Full embed URL; defaults to `<origin>/embeds/<id>.html`. */
  src?: string
}

const ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export const normalizeChartId = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const isValidChartId = (id: string): boolean => ID_PATTERN.test(id)

export const defaultChartSrc = (id: string, origin: string): string =>
  `${origin.replace(/\/$/, '')}/embeds/${id}.html`

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const unescapeHtml = (value: string): string =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')

// Inline styles because the public site has no CSS for our classes.
const IFRAME_STYLE =
  'width:100%;aspect-ratio:16/11;min-height:400px;border:1px solid #e5e5e5;border-radius:6px;display:block'

export const buildChartBlockHtml = ({
  id,
  title,
  src,
}: ChartBlockConfig): string => {
  if (!isValidChartId(id)) {
    throw new Error(
      `Invalid chart id "${id}" — use lowercase letters, digits and dashes`,
    )
  }
  if (src && !/^https:\/\//.test(src)) {
    throw new Error(`Chart src must be an https:// URL, got "${src}"`)
  }

  const attrs = [
    `class="${CHART_BLOCK_CLASS}"`,
    `data-chart="${id}"`,
    `src="${escapeHtml(src || `/embeds/${id}.html`)}"`,
    `title="${escapeHtml(title || id)}"`,
    'loading="lazy"',
    `style="${IFRAME_STYLE}"`,
  ].join(' ')

  return `<iframe ${attrs}></iframe><p><br></p>`
}

const BLOCK_PATTERN = /<iframe\b[^>]*\bdata-chart="([^"]*)"[^>]*>/g
const ATTR_PATTERN = (name: string) => new RegExp(`\\b${name}="([^"]*)"`)

/** Lists every chart embed in stored HTML. */
export const findChartBlocks = (html: string): ChartBlockConfig[] => {
  const blocks: ChartBlockConfig[] = []
  for (const match of html.matchAll(BLOCK_PATTERN)) {
    const tag = match[0]
    const config: ChartBlockConfig = { id: match[1] }
    const title = tag.match(ATTR_PATTERN('title'))?.[1]
    const src = tag.match(ATTR_PATTERN('src'))?.[1]
    if (title) config.title = unescapeHtml(title)
    if (src) config.src = unescapeHtml(src)
    blocks.push(config)
  }
  return blocks
}
